import '@/assets/styles/modal.scss';
import { h, reactive, App, Component, defineComponent } from 'vue';
import { VNode } from '@vue/runtime-core';
import UIModal from '@/components/ui/UIModal/UIModal.vue';
import UIModalNarrow from '@/components/ui/UIModalNarrow/UIModalNarrow.vue';

interface ModalOptions {
    component: Component,
    props?: Record<string, any>,
    onClose?: () => void,
    modalType?: 'default' | 'narrow',
    slots?: {
        [slotName: string]: () => ReturnType<typeof h> | string,
    },
}

interface ModalPluginType {
    open(options: ModalOptions): void,
    close(): void,
}

const modalState = reactive({
    isOpen: false,
    component: null as Component | null,
    props: {} as Record<string, any>,
    modalType: 'default' as 'default' | 'narrow',
    onClose: undefined as (() => void) | undefined,
    slots: {} as ModalOptions['slots'],
});

const ModalPlugin: ModalPluginType = {
    open({ component, props = {}, onClose, modalType = 'default', slots }: ModalOptions): void {
        if (window.screen.width > 768) {
            window.scrollTo({
                top: 0,
                behavior: 'smooth',
            });
        }

        modalState.isOpen = true;
        modalState.component = component;
        modalState.props = props;
        modalState.modalType = modalType;
        modalState.onClose = onClose;
        modalState.slots = slots ?? {};

        // document.body.classList.add('no-scroll');
    },
    close(): void {
        modalState.isOpen = false;
        modalState.component = null;
        modalState.props = {};
        modalState.modalType = 'default';
        modalState.onClose?.();

        // document.body.classList.remove('no-scroll');
    },
};

// noinspection JSUnusedGlobalSymbols
const ModalRoot = {
    install(app: App): void {
        // Глобальные свойства
        app.config.globalProperties.$modal = ModalPlugin;
        app.provide('$modal', ModalPlugin);

        const ModalRootView = defineComponent({
            name: 'ModalRootView',
            setup: () => {
                // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
                return () => {
                    if (!modalState.isOpen || !modalState.component) {
                        return null;
                    }

                    const ModalComponent = modalState.modalType === 'narrow' ? UIModalNarrow : UIModal;

                    const childVNode = h(
                        modalState.component!,
                        { onClose: ModalPlugin.close, ...modalState.props },
                        modalState.slots,
                    );

                    const defaultSlotForModal = (): VNode => childVNode;

                    const slotsObject = {
                        default: defaultSlotForModal,
                    };

                    return h(
                        ModalComponent,
                        {
                            isOpen: modalState.isOpen,
                            onClose: ModalPlugin.close,
                        },
                        slotsObject,
                    );
                };
            },
        });

        app.component('ModalRootView', ModalRootView);
    },
};

export default ModalRoot;
export { ModalPlugin, ModalPluginType };
